import { useId } from "react"
import { t } from "i18next"
import { FormError } from "@/components/ui/form"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"

interface PrefixSuffixInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  label: string
  assistiveText?: string | React.ReactNode
  error?: string
  translateError?: boolean
  errorClasses?: React.HTMLAttributes<HTMLParagraphElement>["className"]
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  wrapperClasses?: string
  "data-testid"?: string
  ref?: React.Ref<HTMLInputElement>
}

export const PrefixSuffixInput = ({
  className,
  wrapperClasses,
  label,
  prefix,
  suffix,
  assistiveText,
  error,
  translateError,
  required,
  errorClasses,
  "data-testid": dataTestId,
  ...props
}: PrefixSuffixInputProps) => {
  const id = useId()

  return (
    <div className={cn(wrapperClasses)} data-testid={dataTestId && dataTestId}>
      <div
        className={cn(
          "border-border bg-surface-subdued [&:has(input:focus)]:outline-primary flex h-12 rounded-md border [&:has(input:focus)]:outline-2 [&:has(input:focus)]:outline-offset-1 [&:has(input:focus)]:outline-solid",
          error && "outline-critical! outline-1 outline-offset-0 outline-solid",
        )}
        data-testid={dataTestId && normalizeTestId(dataTestId, "-group")}>
        {prefix && prefix}
        <div className={cn("border-border relative flex-1", prefix && "border-l", suffix && "border-r")}>
          <input
            id={id}
            autoComplete="off"
            aria-autocomplete="none"
            placeholder=" "
            className={cn(
              className,
              "peer bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-subdued-hovered z-1 h-full w-full px-3.75 pt-4.75 text-sm transition-all focus:outline-0!",
              error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
              prefix && "rounded-l-none",
              suffix && "rounded-r-none",
            )}
            data-testid={dataTestId && normalizeTestId(dataTestId, "-input")}
            {...props}
          />
          <label
            htmlFor={id}
            className={cn(
              "text-on-surface-subdued peer-focus:text-primary absolute top-1 left-4 text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs",
              error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
            )}
            data-testid={dataTestId && normalizeTestId(dataTestId, "-label")}>
            {label}
            {required && "*"}
          </label>
        </div>
        {suffix && suffix}
      </div>
      {assistiveText && !error && (
        <p
          className="text-on-surface-subdued mt-0.75 ml-4 text-xs tracking-normal"
          data-testid={dataTestId && normalizeTestId(dataTestId, "-assistive")}>
          {assistiveText}
        </p>
      )}
      {error && (
        <FormError className={errorClasses} data-testid={dataTestId && normalizeTestId(dataTestId, "-error")}>
          {translateError ? t(error) : error}
        </FormError>
      )}
    </div>
  )
}
